import { useLocation } from 'react-router-dom';
import { useAuth } from '@amzn/neura-web-shared-components';
import { usePermissions } from '../hooks/usePermissions';
import { UserRole, Permission } from '@amzn/neura-knowledge-management-model';
import { trackEvent } from '../monitoring';
import Box from '@cloudscape-design/components/box';
import SpaceBetween from '@cloudscape-design/components/space-between';

interface PermissionRouteGuardProps {
  children: React.ReactNode;
  requiredRole?: UserRole;
  requiredPermission?: Permission;
  redirectTo?: string;
}

export const PermissionRouteGuard: React.FC<PermissionRouteGuardProps> = ({
  children,
  requiredRole,
  requiredPermission,
}) => {
  const { hasPermission, isAtLeastRole, role } = usePermissions();
  const { user } = useAuth();
  const location = useLocation();

  let allowed = true;

  if (requiredPermission) {
    allowed = hasPermission(requiredPermission);
  }

  if (requiredRole) {
    allowed = allowed && isAtLeastRole(requiredRole);
  }

  if (!allowed) {
    const denialDetails = {
      userId: user?.sub ?? 'unknown',
      attemptedRoute: location.pathname,
      userRole: role,
    };

    trackEvent('rbac_access_denied', denialDetails);
    console.warn('[Neura] Access denied', denialDetails);

    return (
      <div style={{ textAlign: 'center', padding: '80px 0' }}>
        <SpaceBetween size="s" alignItems="center">
          <Box variant="h1">Access Denied</Box>
          <Box color="text-body-secondary">
            You do not have permission to access this page. Contact your administrator if you believe this is an error.
          </Box>
          <Box color="text-body-secondary" fontSize="body-s">
            Your role: {role ?? 'Unknown'}
          </Box>
        </SpaceBetween>
      </div>
    );
  }

  return <>{children}</>;
};
